import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useCallback, useEffect, useState } from 'react';
import { $getSelection, $isRangeSelection } from 'lexical';
import {
  $isListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListNode,
  REMOVE_LIST_COMMAND,
} from '@lexical/list';
import { $getNearestNodeOfType, mergeRegister } from '@lexical/utils';
import { richTextEditorStrings } from '../../../i18n/components/richtTextEditor';

type ListType = 'ul' | 'ol' | 'none';

export function ListPlugin() {
  const [editor] = useLexicalComposerContext();
  const [listType, setListType] = useState<ListType>('none');

  const updateListType = useCallback(() => {
    const selection = $getSelection();

    if (!$isRangeSelection(selection)) {
      return;
    }

    const anchorNode = selection.anchor.getNode();
    const isRootNode = anchorNode.getKey() === 'root';
    const element = isRootNode
      ? anchorNode
      : anchorNode.getTopLevelElementOrThrow();

    if (!$isListNode(element)) {
      setListType('none');
      return;
    }

    const parentList = $getNearestNodeOfType(anchorNode, ListNode);
    const nodeListType = parentList
      ? parentList.getListType()
      : element.getListType();
    const listTypeValue = nodeListType === 'bullet' ? 'ul' : 'ol';
    setListType(listTypeValue);
  }, []);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateListType();
        });
      }),
    );
  }, [editor, updateListType]);

  const formatBulletList = () => {
    const command =
      listType === 'ul' ? REMOVE_LIST_COMMAND : INSERT_UNORDERED_LIST_COMMAND;
    editor.dispatchCommand(command, undefined);
  };

  const formatOrderedList = () => {
    const command =
      listType === 'ol' ? REMOVE_LIST_COMMAND : INSERT_ORDERED_LIST_COMMAND;
    editor.dispatchCommand(command, undefined);
  };

  return (
    <span className="lexical-toolbar-group">
      <button
        type="button"
        onClick={formatOrderedList}
        className={`lexical-toolbar-button ${listType === 'ol' ? 'active' : ''}`}
        aria-label={richTextEditorStrings.list.ordered.label}
        title={richTextEditorStrings.list.ordered.title}
      >
        <i className="pi pi-sort-numeric-down" />
      </button>
      <button
        type="button"
        onClick={formatBulletList}
        className={`lexical-toolbar-button ${listType === 'ul' ? 'active' : ''}`}
        aria-label={richTextEditorStrings.list.unordered.label}
        title={richTextEditorStrings.list.unordered.title}
      >
        <i className="pi pi-list" />
      </button>
    </span>
  );
}
